import { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AUTOLOGIN_JWT_PENDING_KEY } from '../auth/preflightAuth';
import { userManager } from '../auth/userManager';
import EmailLinkTracker from '../components/EmailLinkTracker';
import DarkLoadingSpinner from '../components/LoadingSpinner/DarkLoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import PrivateLayout from '../layouts/PrivateLayout';
import { isKeycloakLoginEnabled } from '../util/constants';
import { logger } from '../util/logger';
import { ROUTE_PATHS } from './routePaths';


const PrivateRoute = () => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const useKeycloak = isKeycloakLoginEnabled();

  useEffect(() => {
    // Only trigger the Keycloak redirect once auth state is confirmed (not loading)
    // and the user is definitively unauthenticated.
    if (!loading && !isAuthenticated && useKeycloak) {
      const redirectPath = location.pathname + location.search;
      localStorage.setItem('pendingRedirectUrl', redirectPath);

      const pendingJwt = new URLSearchParams(location.search).get('autologin');
      if (pendingJwt) {
        logger.log('[PrivateRoute] Auto-login — signinRedirect with autologin param');
        sessionStorage.setItem(AUTOLOGIN_JWT_PENDING_KEY, pendingJwt);
        void userManager.signinRedirect({
          extraQueryParams: { autologin: pendingJwt, prompt: 'none' },
        });
        return;
      }

      if (sessionStorage.getItem('silentSsoSuccess')) {
        void navigate(ROUTE_PATHS.LOGIN, { replace: true });
        return;
      }

      void userManager.signinRedirect();
    }
  }, [isAuthenticated, loading, useKeycloak, location, navigate]);

  if (loading) {
    return <DarkLoadingSpinner />;
  }

  if (!isAuthenticated) {
    if (useKeycloak) {
      if (sessionStorage.getItem('silentSsoSuccess')) {
        return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
      }
      // Spinner stays visible while the browser is in-flight to Keycloak.
      return <DarkLoadingSpinner />;
    }
    const redirectPath = location.pathname + location.search;
    localStorage.setItem('pendingRedirectUrl', redirectPath);
    return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
  }

  return (
    <PrivateLayout>
      <EmailLinkTracker />
      <Outlet />
    </PrivateLayout>
  );
};

export default PrivateRoute;
